import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { LEAVE_STATUS, UPLOAD_BASE, canCancelOwnLeave, fmtDateTH, fmtDays, toBE } from './hrShared'

const emptyForm = { lt_id: '', lr_start_date: '', lr_end_date: '', lr_days: '', lr_reason: '', file: null }

// การ์ดประวัติการลา 1 รายการ สำหรับมุมมองมือถือ
const MyLeaveCard = ({ r, onCancel }) => {
  const ls = LEAVE_STATUS[r.lr_status] || LEAVE_STATUS.pending
  return (
    <div className="border rounded p-3">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="fw-semibold">{r.lt_name}</div>
        <Badge bg={ls.bg}>{ls.label}</Badge>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">วันที่ลา:</span> {fmtDateTH(r.lr_start_date)} - {fmtDateTH(r.lr_end_date)} ({fmtDays(r.lr_days)} วัน)</div>
        {r.lr_reason && <div><span className="text-muted">เหตุผล:</span> {r.lr_reason}</div>}
        {r.lr_attachment && (
          <div>
            <a href={`${UPLOAD_BASE}/uploads/hr_leave/${r.lr_attachment}`} target="_blank" rel="noreferrer">
              <i className="ri-attachment-2 me-1" />ไฟล์แนบ
            </a>
          </div>
        )}
        {r.approved_by_name && <div className="text-muted fs-xs mt-1">{r.lr_status === 'approved' ? 'ผู้อนุมัติ' : 'ผู้พิจารณา'}: {r.approved_by_name}</div>}
        {r.approve_note && <div className="text-muted fs-xs mt-1">{r.approve_note}</div>}
      </div>
      <div className="mt-2 pt-2 border-top d-flex gap-2">
        <Button as={Link} to={`/hr/leave/${r.lr_id}/print`} size="sm" variant="outline-secondary" className="flex-grow-1">
          <i className="ri-printer-line me-1" />พิมพ์ใบลา
        </Button>
        {canCancelOwnLeave(r) && (
          <Button size="sm" variant="outline-danger" className="flex-grow-1" onClick={onCancel}>ยกเลิก</Button>
        )}
      </div>
    </div>
  )
}

export default function MyLeave() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(String(currentYear))
  const [data, setData] = useState({ usage: [], requests: [] })
  const [leaveTypes, setLeaveTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [daysEdited, setDaysEdited] = useState(false)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/hr/me/leave?year=${year}`)
      if (res.data.success) setData(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    axiosInstance.get('/api/hr/me/leave-types')
      .then(res => { if (res.data.success) setLeaveTypes(res.data.data) })
      .catch(() => {})
  }, [])

  // Auto-fill days = inclusive date difference, editable for half days
  useEffect(() => {
    if (daysEdited || !form.lr_start_date || !form.lr_end_date) return
    const start = new Date(form.lr_start_date)
    const end = new Date(form.lr_end_date)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return
    const days = Math.round((end - start) / 86400000) + 1
    setForm(f => ({ ...f, lr_days: String(days) }))
  }, [form.lr_start_date, form.lr_end_date, daysEdited])

  const selectedType = leaveTypes.find(t => String(t.lt_id) === String(form.lt_id))

  const openModal = () => {
    setForm(emptyForm)
    setDaysEdited(false)
    setModalError('')
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      fd.append('lt_id', form.lt_id)
      fd.append('lr_start_date', form.lr_start_date)
      fd.append('lr_end_date', form.lr_end_date)
      fd.append('lr_days', form.lr_days)
      fd.append('lr_reason', form.lr_reason)
      if (form.file) fd.append('attachment', form.file)
      const res = await axiosInstance.post('/api/hr/me/leave', fd)
      if (res.data.success) {
        setShowModal(false)
        Swal.fire({ icon: 'success', title: 'ยื่นใบลาแล้ว', text: 'รอการอนุมัติจากหัวหน้าบุคลากร', timer: 2000, showConfirmButton: false })
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'ยื่นใบลาไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  // ใบที่อนุมัติแล้วเป็นคนละเรื่องกับใบที่ยังไม่มีใครดู — หัวหน้าบุคลากรอนุมัติไปแล้ว
  // และฝ่ายการเรียนการสอนอาจจัดครูสอนแทนไว้ กล่องยืนยันจึงต้องบอกให้ครบก่อนกด
  const handleCancel = async (r) => {
    const approved = r.lr_status === 'approved'
    const result = await Swal.fire({
      icon: 'warning',
      title: approved ? 'ยกเลิกใบลาที่อนุมัติแล้ว?' : 'ยกเลิกคำขอลา?',
      html: `${r.lt_name} ${fmtDateTH(r.lr_start_date)} - ${fmtDateTH(r.lr_end_date)}`
        + (approved
          ? '<div class="text-muted small mt-2">ระบบจะแจ้งฝ่ายบุคลากร และแจ้งครูที่รับสอนแทนคาบของท่าน (ถ้ามี) ให้ทราบ</div>'
          : ''),
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกใบลา',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      const res = await axiosInstance.put(`/api/hr/me/leave/${r.lr_id}/cancel`)
      fetchData()
      // คาบที่จัดสอนแทนไว้ไม่ถูกยกเลิกให้เอง — ต้องเป็นกล่องที่กดรับทราบ ไม่ใช่ toast ที่หายเอง
      const subs = res.data?.data?.substitutions || 0
      if (subs > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'ยกเลิกใบลาแล้ว — แต่ยังมีการจัดสอนแทนค้างอยู่',
          text: `ช่วงวันลานี้มีคาบที่จัดครูสอนแทนไว้ ${subs} คาบ กรุณาแจ้งฝ่ายการเรียนการสอนให้ยกเลิกการสอนแทน`,
          confirmButtonText: 'รับทราบ',
        })
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ยกเลิกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2]

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-calendar-check-line me-2 text-primary" />
              การลาของฉัน
            </h4>
            <div className="d-flex gap-2">
              <Form.Select size="sm" style={{ width: 130 }} value={year} onChange={e => setYear(e.target.value)}>
                {yearOptions.map(y => <option key={y} value={y}>พ.ศ. {toBE(y)}</option>)}
              </Form.Select>
              <Button variant="primary" size="sm" onClick={openModal}>
                <i className="ri-add-line me-1" />ยื่นใบลา
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <>
              {/* Quota cards */}
              <Row className="g-3 mb-4">
                {data.usage.map(u => {
                  const quota = u.lt_annual_quota === null ? null : parseFloat(u.lt_annual_quota)
                  const used = parseFloat(u.used_days) || 0
                  const remaining = quota === null ? null : quota - used
                  return (
                    <Col xs={6} md={4} lg={2} key={u.lt_id}>
                      <Card className="card-one h-100">
                        <Card.Body className="p-3">
                          <div className="fs-sm text-muted text-truncate" title={u.lt_name}>{u.lt_name}</div>
                          <div className="d-flex align-items-baseline gap-1 mt-1">
                            <h4 className="mb-0">{fmtDays(used)}</h4>
                            <span className="text-muted fs-sm">/ {quota === null ? 'ไม่จำกัด' : fmtDays(quota)} วัน</span>
                          </div>
                          {quota !== null && (
                            <div className={`fs-sm mt-1 ${remaining <= 0 ? 'text-danger' : 'text-success'}`}>
                              คงเหลือ {fmtDays(Math.max(remaining, 0))} วัน
                            </div>
                          )}
                          {parseFloat(u.pending_days) > 0 && (
                            <div className="fs-sm text-warning">รออนุมัติ {fmtDays(u.pending_days)} วัน</div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  )
                })}
              </Row>

              {/* History */}
              <Card className="card-one">
                <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                  <Card.Title as="h6" className="mb-0">ประวัติการลา ปี พ.ศ. {toBE(year)}</Card.Title>
                  <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                    <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                      onClick={() => setView('table')} title="มุมมองตาราง">
                      <i className="ri-table-line" />
                    </button>
                    <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                      onClick={() => setView('card')} title="มุมมองการ์ด">
                      <i className="ri-layout-grid-line" />
                    </button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {data.requests.length === 0 ? (
                    <div className="text-center text-muted py-4">ยังไม่มีคำขอลาในปีนี้</div>
                  ) : view === 'card' ? (
                    <div className="p-3">
                      <Row className="g-3">
                        {data.requests.map(r => (
                          <Col key={r.lr_id} xs={12} md={6} xl={4}>
                            <MyLeaveCard r={r} onCancel={() => handleCancel(r)} />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th>ประเภท</th>
                        <th>วันที่ลา</th>
                        <th className="text-end">วัน</th>
                        <th>เหตุผล</th>
                        <th>ไฟล์แนบ</th>
                        <th>สถานะ</th>
                        <th style={{ width: 140 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.requests.map(r => {
                        const ls = LEAVE_STATUS[r.lr_status] || LEAVE_STATUS.pending
                        return (
                          <tr key={r.lr_id}>
                            <td>{r.lt_name}</td>
                            <td>{fmtDateTH(r.lr_start_date)} - {fmtDateTH(r.lr_end_date)}</td>
                            <td className="text-end">{fmtDays(r.lr_days)}</td>
                            <td className="text-truncate" style={{ maxWidth: 200 }} title={r.lr_reason || ''}>{r.lr_reason || '-'}</td>
                            <td>
                              {r.lr_attachment ? (
                                <a href={`${UPLOAD_BASE}/uploads/hr_leave/${r.lr_attachment}`} target="_blank" rel="noreferrer">
                                  <i className="ri-attachment-2" />
                                </a>
                              ) : '-'}
                            </td>
                            <td>
                              <Badge bg={ls.bg}>{ls.label}</Badge>
                              {r.approved_by_name && (
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.lr_status === 'approved' ? 'ผู้อนุมัติ' : 'ผู้พิจารณา'}: {r.approved_by_name}</div>
                              )}
                              {r.approve_note && (
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.approve_note}</div>
                              )}
                            </td>
                            <td className="text-end text-nowrap">
                              <Button as={Link} to={`/hr/leave/${r.lr_id}/print`} size="sm"
                                variant="outline-secondary" className="me-1" title="พิมพ์แบบใบลา">
                                <i className="ri-printer-line" />
                              </Button>
                              {canCancelOwnLeave(r) && (
                                <Button size="sm" variant="outline-danger" onClick={() => handleCancel(r)}>
                                  ยกเลิก
                                </Button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>
      <Footer />

      {/* Submit-leave modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">ยื่นใบลา</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}

            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">ประเภทการลา</Form.Label>
              <Form.Select size="sm" required value={form.lt_id}
                onChange={e => setForm(f => ({ ...f, lt_id: e.target.value }))}>
                <option value="">— เลือกประเภทการลา —</option>
                {leaveTypes.map(t => (
                  <option key={t.lt_id} value={t.lt_id}>
                    {t.lt_name}{t.lt_annual_quota !== null ? ` (โควตา ${fmtDays(t.lt_annual_quota)} วัน/ปี)` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row className="g-2 mb-2">
              <Col xs={5}>
                <Form.Label className="fs-sm mb-1">ตั้งแต่วันที่</Form.Label>
                <Form.Control size="sm" type="date" required value={form.lr_start_date}
                  onChange={e => setForm(f => ({ ...f, lr_start_date: e.target.value }))} />
              </Col>
              <Col xs={5}>
                <Form.Label className="fs-sm mb-1">ถึงวันที่</Form.Label>
                <Form.Control size="sm" type="date" required value={form.lr_end_date} min={form.lr_start_date || undefined}
                  onChange={e => setForm(f => ({ ...f, lr_end_date: e.target.value }))} />
              </Col>
              <Col xs={2}>
                <Form.Label className="fs-sm mb-1">จำนวนวัน</Form.Label>
                <Form.Control size="sm" type="number" min="0.5" step="0.5" required value={form.lr_days}
                  onChange={e => { setForm(f => ({ ...f, lr_days: e.target.value })); setDaysEdited(true) }} />
              </Col>
            </Row>
            <Form.Text className="text-muted d-block mb-2">
              จำนวนวันคำนวณอัตโนมัติจากช่วงวันที่ — แก้ไขได้กรณีลาครึ่งวัน (เช่น 0.5)
            </Form.Text>

            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">เหตุผลการลา</Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} value={form.lr_reason}
                onChange={e => setForm(f => ({ ...f, lr_reason: e.target.value }))} />
            </Form.Group>

            <Form.Group>
              <Form.Label className="fs-sm mb-1">
                ไฟล์แนบ (PDF/Word/รูปภาพ ≤ 10MB)
                {selectedType?.lt_require_attachment ? <span className="text-danger"> *จำเป็น</span> : <span className="text-muted"> — ไม่บังคับ</span>}
              </Form.Label>
              <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,image/*"
                required={!!selectedType?.lt_require_attachment}
                onChange={e => setForm(f => ({ ...f, file: e.target.files[0] || null }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-send-plane-line me-1" />}
              ยื่นใบลา
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
